import { AnchorWallet } from "@solana/wallet-adapter-react";
import { PublicKey, SystemProgram, Transaction } from "@solana/web3.js";
import {
  WhitelistingAction,
} from "../../common/enums/whitelisting.enum";
import {
  monkeAdminSeed,
  monkeAuthoritySeed,
  unqClubSeed,
} from "../common/seed";
import { programFactory, RPC_CONNECTION } from "../common/utilities";
import { sendTransaction } from "../transactions/sendTransaction";
import { chunk } from "lodash";
import { IInstructionSet } from "../../common/interfaces/common.interface";
import { sendTransactions } from "../transactions/sendTransactions";
import { SequenceType } from "../../common/enums/common.enums";

export const changeRequestStatus = async (
  userWallet: string,
  wallet: AnchorWallet,
  action: WhitelistingAction
) => {
  try {
    const clubProgram = programFactory(wallet);

    const [monkeWhitelisting] = PublicKey.findProgramAddressSync(
      [unqClubSeed, monkeAuthoritySeed],
      clubProgram.programId
    );

    const [monkeAdmins] = PublicKey.findProgramAddressSync(
      [unqClubSeed, monkeAdminSeed],
      clubProgram.programId
    );

    const approveMember = await clubProgram.methods
      .addWhitelistings([new PublicKey(userWallet)], action)
      .accounts({
        payer: wallet.publicKey,
        systemProgram: SystemProgram.programId,
        whitelistedMembers: monkeWhitelisting,
        monkeAdmins,
      })
      .instruction();

    const tx = new Transaction({
      feePayer: wallet.publicKey,
      recentBlockhash: (await RPC_CONNECTION.getLatestBlockhash()).blockhash,
    });
    tx.add(approveMember);
    const signedTx = await wallet.signTransaction(tx);
    await sendTransaction({
      transaction: signedTx,
      connection: RPC_CONNECTION,
      wallet,
    });
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getWhitelistings = async (
  wallet: AnchorWallet
): Promise<PublicKey[]> => {
  try {
    const clubProgram = programFactory(wallet);

    const [monkeWhitelisting] = PublicKey.findProgramAddressSync(
      [unqClubSeed, monkeAuthoritySeed],
      clubProgram.programId
    );
    const whitelistingData = await clubProgram.account.whitelistingData.fetch(
      monkeWhitelisting
    );
    return whitelistingData.whitelistedMembers;
  } catch (error) {
    return [];
  }
};

export const addBulkWhitelistings = async (
  pubkeys: PublicKey[],
  wallet: AnchorWallet
) => {
  const chunkedPubkeys = chunk(pubkeys, 20);
  const clubProgram = programFactory(wallet);
  const [whitelistedMembers] = PublicKey.findProgramAddressSync(
    [unqClubSeed, monkeAuthoritySeed],
    clubProgram.programId
  );
  const [monkeAdmins] = PublicKey.findProgramAddressSync(
    [unqClubSeed, monkeAdminSeed],
    clubProgram.programId
  );
  try {
    const allInstructions: IInstructionSet[] = [];
    for (const [index, pk] of chunkedPubkeys.entries()) {
      const addPubkeys = await clubProgram.methods
        .addWhitelistings(pk, WhitelistingAction.Add)
        .accounts({
          payer: wallet.publicKey,
          systemProgram: SystemProgram.programId,
          whitelistedMembers,
          monkeAdmins,
        })
        .instruction();
      allInstructions.push({
        instructions: [addPubkeys],
        description: "Adding list of pubkeys",
      });
    }
    await sendTransactions(
      RPC_CONNECTION,
      wallet,
      allInstructions,
      SequenceType.Sequential
    );
  } catch (error) {
    console.log(error);
    throw error;
  }
};

export const getMonkeAdmins = async (wallet: AnchorWallet) => {
  try {
    const clubProgram = programFactory(wallet);
    const [monkeAdmins] = PublicKey.findProgramAddressSync(
      [unqClubSeed, monkeAdminSeed],
      clubProgram.programId
    );
    const monkeAdminAccount = await clubProgram.account.monkeAdmins.fetch(
      monkeAdmins
    );
    return monkeAdminAccount.adminConfigs as any[];
  } catch (error) {
    return [];
  }
};
